/**
 * OBGYNPanel — additive top card for specialty.mode === 'soap+obgyn'
 * ──────────────────────────────────────────────────────────────────
 * Sits ABOVE the standard SOAPPanel rather than replacing it. Gives
 * OB/GYN providers at-a-glance prenatal context while the normal
 * SOAP workflow stays intact.
 *
 * All numbers / dates are mock surface-level. Gestational-age
 * calculation below is accurate math so the UI feels real.
 */
import { useMemo, useState } from 'react';
import {
  Baby, HeartPulse, Ruler, Syringe, ClipboardCheck, Calendar, Activity,
} from 'lucide-react';
import { toast } from 'sonner';

const TODAY = new Date();

const daysBetween = (a, b) => Math.floor((b - a) / 86400000);

/* Derive GA + EDD from LMP (Naegele: LMP + 280 days). */
const derive = (lmpStr) => {
  const lmp = new Date(lmpStr);
  if (Number.isNaN(lmp.getTime())) return { weeks: '', days: '', edd: '' };
  const delta = daysBetween(lmp, TODAY);
  const weeks = Math.floor(delta / 7);
  const days = delta % 7;
  const edd = new Date(lmp.getTime() + 280 * 86400000);
  return {
    weeks, days,
    ga: `${weeks} wks ${days} d`,
    edd: edd.toISOString().slice(0, 10),
    trimester: weeks < 13 ? '1st' : weeks < 27 ? '2nd' : '3rd',
  };
};

/* Default prenatal lab checklist per trimester. */
const PRENATAL_LABS = [
  { id: 't1_cbc', label: 'CBC + Type & screen',  trimester: '1st', done: true  },
  { id: 't1_hiv', label: 'HIV + syphilis + Hep B', trimester: '1st', done: true  },
  { id: 't1_rub', label: 'Rubella + varicella IgG', trimester: '1st', done: true  },
  { id: 't1_us',  label: 'Dating ultrasound',    trimester: '1st', done: true  },
  { id: 't2_anat',label: 'Anatomy scan (18–20 wk)', trimester: '2nd', done: true  },
  { id: 't2_gct', label: '1-hour GCT (24–28 wk)', trimester: '2nd', done: false },
  { id: 't3_gbs', label: 'GBS swab (35–37 wk)',   trimester: '3rd', done: false },
  { id: 't3_tdap',label: 'Tdap (27–36 wk)',       trimester: '3rd', done: false },
];

const Field = ({ label, children, testId }) => (
  <div className="space-y-0.5" data-testid={testId}>
    <p className="text-[9px] uppercase tracking-wide text-slate-400">{label}</p>
    {children}
  </div>
);

const Input = (props) => (
  <input
    {...props}
    className={`w-full text-[11px] px-2 py-1.5 rounded border border-slate-200 bg-slate-50/60 focus:bg-white focus:border-slate-400 outline-none ${props.className || ''}`}
  />
);

export const OBGYNPanel = () => {
  const [gravida, setGravida] = useState(2);
  const [para,    setPara]    = useState(1);
  const [lmp,     setLmp]     = useState('2025-10-08'); // ~ 28w today-ish
  const [fht,     setFht]     = useState(148);
  const [fundal,  setFundal]  = useState(29);
  const [gbs,     setGbs]     = useState('pending');
  const [labs,    setLabs]    = useState(PRENATAL_LABS);

  const { weeks, days, ga, edd, trimester } = useMemo(() => derive(lmp), [lmp]);

  const toggleLab = (id) => setLabs((prev) => prev.map((l) => l.id === id ? { ...l, done: !l.done } : l));
  const doneCount = labs.filter((l) => l.done).length;

  return (
    <div className="rounded-xl border border-rose-200 bg-rose-50/30 p-4 space-y-3" data-testid="obgyn-panel">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Baby className="h-4 w-4 text-rose-700" />
        <p className="text-xs font-bold text-slate-900">OB prenatal dashboard</p>
        <span className="ml-2 text-[9px] px-2 py-0.5 rounded-full bg-rose-100 text-rose-800 font-semibold">
          G{gravida}P{para} · {ga || '—'} · {trimester}
        </span>
        <button
          type="button"
          onClick={() => toast.success('Added OB summary to note')}
          className="ml-auto text-[10px] font-semibold text-slate-600 hover:text-slate-900 px-2 py-1 rounded bg-white border border-slate-200"
          data-testid="obgyn-add-to-note"
        >
          + Add to note
        </button>
      </div>

      {/* Row 1 — core numbers */}
      <div className="grid grid-cols-6 gap-3 p-3 rounded-lg bg-white border border-slate-100">
        <Field label="Gravida" testId="ob-gravida">
          <Input type="number" min={0} max={20} value={gravida} onChange={(e) => setGravida(+e.target.value)} />
        </Field>
        <Field label="Para" testId="ob-para">
          <Input type="number" min={0} max={20} value={para} onChange={(e) => setPara(+e.target.value)} />
        </Field>
        <Field label="LMP" testId="ob-lmp">
          <Input type="date" value={lmp} onChange={(e) => setLmp(e.target.value)} />
        </Field>
        <Field label="GA (today)" testId="ob-ga">
          <p className="text-[12px] font-bold text-slate-900 mt-1 tabular-nums">{weeks}<span className="text-[10px] font-normal">w</span> {days}<span className="text-[10px] font-normal">d</span></p>
        </Field>
        <Field label="EDD (Naegele)" testId="ob-edd">
          <p className="text-[11px] font-medium text-slate-800 mt-1">{edd}</p>
        </Field>
        <Field label="Trimester" testId="ob-trimester">
          <p className="text-[11px] font-medium text-slate-800 mt-1">{trimester}</p>
        </Field>
      </div>

      {/* Row 2 — vitals & surveillance */}
      <div className="grid grid-cols-3 gap-3">
        <div className="p-3 rounded-lg bg-white border border-slate-100 flex items-center gap-3">
          <Ruler className="h-3.5 w-3.5 text-slate-500" />
          <Field label="Fundal height (cm)" testId="ob-fundal-wrap">
            <Input type="number" value={fundal} onChange={(e) => setFundal(+e.target.value)} data-testid="ob-fundal" />
          </Field>
          <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${Math.abs(fundal - weeks) <= 3 ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
            {Math.abs(fundal - weeks) <= 3 ? 'WNL' : 'check growth'}
          </span>
        </div>
        <div className="p-3 rounded-lg bg-white border border-slate-100 flex items-center gap-3">
          <HeartPulse className="h-3.5 w-3.5 text-rose-600" />
          <Field label="FHT (bpm)" testId="ob-fht-wrap">
            <Input type="number" value={fht} onChange={(e) => setFht(+e.target.value)} data-testid="ob-fht" />
          </Field>
          <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${fht >= 110 && fht <= 160 ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>
            {fht >= 110 && fht <= 160 ? 'reassuring' : 'abnormal'}
          </span>
        </div>
        <div className="p-3 rounded-lg bg-white border border-slate-100 flex items-center gap-3">
          <Syringe className="h-3.5 w-3.5 text-indigo-600" />
          <Field label="GBS status" testId="ob-gbs-wrap">
            <select
              value={gbs}
              onChange={(e) => setGbs(e.target.value)}
              className="w-full text-[11px] px-2 py-1.5 rounded border border-slate-200 bg-slate-50/60 focus:bg-white focus:border-slate-400 outline-none"
              data-testid="ob-gbs"
            >
              <option value="pending">Pending</option>
              <option value="positive">Positive</option>
              <option value="negative">Negative</option>
              <option value="unknown">Unknown — treat</option>
            </select>
          </Field>
        </div>
      </div>

      {/* Row 3 — prenatal lab checklist */}
      <div className="p-3 rounded-lg bg-white border border-slate-100" data-testid="ob-lab-checklist">
        <div className="flex items-center gap-2 mb-2">
          <ClipboardCheck className="h-3.5 w-3.5 text-slate-500" />
          <p className="text-[11px] font-bold text-slate-800">Prenatal lab checklist</p>
          <span className="text-[9px] text-slate-400 ml-auto">{doneCount}/{labs.length} complete</span>
        </div>
        <div className="grid grid-cols-2 gap-1.5">
          {labs.map((l) => (
            <label
              key={l.id}
              className={`flex items-center gap-2 px-2 py-1 rounded border cursor-pointer transition ${
                l.done ? 'border-emerald-200 bg-emerald-50/50' : 'border-slate-200 bg-slate-50 hover:bg-slate-100'
              }`}
              data-testid={`ob-lab-${l.id}`}
            >
              <input type="checkbox" checked={l.done} onChange={() => toggleLab(l.id)} className="h-3 w-3 rounded text-emerald-600" />
              <span className="text-[10px] text-slate-400 font-semibold">{l.trimester}</span>
              <span className={`text-[11px] ${l.done ? 'text-slate-800' : 'text-slate-700'}`}>{l.label}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
};

export default OBGYNPanel;
